<template>
    <div
        class="event-availability"
        :class="'event-availability--' + availabilityStatusSlug"
        v-if="availabilityStatus"
        v-cloak
    >
        <div v-if="AVAILABILITY_BAR_STATUSES.includes(availabilityStatus)" class="event-availability__bar"></div>
        {{ availabilityStatus }}
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { slugify } from '../../utils/text';
import { getAvailability } from '../../services/performance';


/**
 * Defines capacity status thresholds for ticket availability.
 * The keys represent the percentage of tickets available, and the values are the corresponding status labels.
 * Be sure to keep the keys in ascending order to ensure correct status assignment.
 *
 * If `availability` is configurable via Basker custom attributes then make sure they match
 */
const CAPACITY_STATUS = {
    0: 'Sold Out',                  // No Availability
    0.1: 'Low Availability',        // Less than 10% of tickets available
    0.2: 'Limited Availability',    // Less than 20% of tickets available
    default: 'Good Availability'    // over 20% of tickets available
};

const AVAILABILITY_BAR_STATUSES = ['Low Availability', 'Limited Availability', 'Good Availability'];

const BASKER_BOOKING_STATUS_MAP = {
    'onSale': 'On Sale',
    'comingSoon': 'Coming Soon',
    'waitlist': 'Waitlist',
    'offSale': 'Off Sale',
    'cancelled': 'Cancelled',
    'soldOut': 'Sold Out',
};

const props = defineProps({
    event: {
        type: Object,
        required: true,
    },
});

const availabilityStatus = computed(() => {
    if (props.event.bookingStatus) {
        // Manually set availability via CMS admin
        return BASKER_BOOKING_STATUS_MAP[props.event.bookingStatus];
    } else if (Object.keys(props.event.tessitura).length) {
        // Calculate availability status (based on venue capacity)
        // Don't calculate if not Tessitura event
        const availability = getAvailability(props.event);

        if (availability === null) {
            return null;
        } else if (availability <= 0) {
            return CAPACITY_STATUS[0];
        } else {
            // Capacity can be set at either the event or venue level, so check both places for a capacity value
            const capacity = props.event.attributes?.custom?.allowance ?
                parseInt(props.event.attributes?.custom?.allowance, 10) :
                parseInt(props.event.event.venue?.attributes?.custom?.capacity, 10);

            if (capacity) {
                const availabilityPercent = availability / capacity;

                const status = Object.keys(CAPACITY_STATUS).find(key => availabilityPercent <= parseFloat(key)) || 'default';
                return CAPACITY_STATUS[status];
            }
        }
    }
});

const availabilityStatusSlug = computed(() => {
    return slugify(availabilityStatus.value);
});

</script>
